import axios from 'axios'
import { store } from '@/store'
import { addNotification } from '@/store/notificationsSlice'
import { config as runtimeConfig } from '@/common/services/runtimeConfig'

import { API_LOGIN_URL } from '@/common/constants/request'
import { setLimitSession, setShowLimitSession } from '@/store/userSlice'
import * as Sentry from '@sentry/nextjs'
import userLanguage from '@/common/services/userLanguage'
import userLocationApi from '@/common/services/userLocationApi'
import qs from 'qs'
import {
  CANCELED_REQUEST,
  SESSION_CLOSED_CODE_NEW,
  SESSION_IS_CLOSED,
} from '@/common/constants/responseCodes'
import baseLogger from '@/common/services/BaseLogger'
import { EXPERIMENT_SESSION_STORAGE_KEY } from '@/common/constants/amplitude'
import { getCookie } from 'cookies-next'
import getConfig from 'next/config'

export const COOKIE_KEY_USER_TEAM = 'user_team'
const QUERY_PARAM_SET_COUNTRY = 'set_country'

const getFullPathUrl = (config) => {
  return `${config.baseURL}${config.url}`
}

const getRequestMethod = (config) => {
  return config.method.toUpperCase()
}

export const createAxiosInstance = ({ getToken, clearToken }) => {
  const instance = axios.create()

  instance.interceptors.request.use((config) => {
    const accessToken = getToken() || config.token
    const needAddUserToken = config.url !== API_LOGIN_URL
    const prodUrl =
      typeof window === 'undefined'
        ? runtimeConfig.serverApiUrl
        : runtimeConfig.clientApiUrl
    const currentUrl = process.env.IS_DEV
      ? getConfig().publicRuntimeConfig.baseAPIClientUrl
      : prodUrl

    if (accessToken && needAddUserToken) {
      config.headers['X-Authorization'] = `Bearer ${accessToken}`
    }

    if (userLanguage.lang) {
      config.headers['X-Language'] = userLanguage.lang
    }

    try {
      if (typeof window !== 'undefined') {
        const urlParams = new URLSearchParams(window.location.search)
        const countryParam = urlParams.get(QUERY_PARAM_SET_COUNTRY)

        if (countryParam) {
          document.cookie = `override_country=${countryParam}; path=/;`

          urlParams.delete(QUERY_PARAM_SET_COUNTRY)

          const queryString = urlParams.toString()
          const newUrl =
            queryString.length > 0
              ? `${window.location.pathname}?${queryString}`
              : window.location.pathname

          window.history.replaceState({}, '', newUrl)
        }
      }
      const teamCookieRaw = getCookie(COOKIE_KEY_USER_TEAM)
      const overrideCountry = getCookie('override_country')

      if (teamCookieRaw && overrideCountry) {
        const { isTeamMember } = JSON.parse(teamCookieRaw)

        if (isTeamMember) {
          config.headers['X-User-Country'] = overrideCountry
        }
      }
    } catch (e) {
      baseLogger.logger.error('Failed to process set_country logic:', e)
    }

    if (typeof window !== 'undefined') {
      const experimentMap = JSON.parse(
        window.sessionStorage[EXPERIMENT_SESSION_STORAGE_KEY] ?? '{}'
      )

      const experiments = Object.keys(experimentMap).reduce((acc, key) => {
        const experiment = {
          test_id: key,
          variant: experimentMap[key],
        }

        acc.push(experiment)
        return acc
      }, [])

      config.headers = {
        ...config.headers,
        ...(experiments.length && {
          'X-Experiment-Override': JSON.stringify(experiments),
        }),
      }
    }

    if (typeof window === 'undefined' && !userLocationApi.isEmpty()) {
      config.headers['X-User-Ip'] = userLocationApi.connectionIp
      config.headers['X-User-country'] = userLocationApi.ipCountry
      config.baseURL = currentUrl
    } else {
      config.baseURL = currentUrl
    }

    Sentry.addBreadcrumb({
      type: 'info',
      category: 'info',
      message: `${getRequestMethod(
        config
      )} before send request to ${getFullPathUrl(config)}`,
      level: 'info',
    })

    if (!config.paramsSerializer) {
      config.paramsSerializer = (params) =>
        qs.stringify(params, { arrayFormat: 'repeat' })
    }

    return config
  })

  instance.interceptors.response.use(
    async (res) => {
      Sentry.addBreadcrumb({
        type: 'info',
        category: 'xhr',
        data: res.data,
        level: 'info',
      })

      Sentry.addBreadcrumb({
        type: 'http',
        category: 'xhr',
        data: {
          url: getFullPathUrl(res.config),
          method: getRequestMethod(res.config),
          status_code: res.status,
        },
        level: 'info',
      })

      return res
    },
    async (error) => {
      const requestError = error?.response?.data?.error

      if (error.code === CANCELED_REQUEST) {
        return Promise.reject({ response: error })
      }

      if (
        error?.response?.status === 403 &&
        requestError?.code === SESSION_CLOSED_CODE_NEW
      ) {
        await store.dispatch(setLimitSession(true))
        await store.dispatch(setShowLimitSession(true))
      }

      const redirectToLogin = () => {
        const loginPath = '/login'

        if (typeof window !== 'undefined') {
          window.location = loginPath
        }
      }

      if (error?.response?.status === 401) {
        baseLogger.setUserToken(null)
        baseLogger.logger.info('before user logout by 401 status')
        store.dispatch(
          addNotification({
            type: 'warning',
            text: 'common.sessionTokenInfoMessage',
          })
        )
        clearToken()
        redirectToLogin()
        return Promise.reject(error)
      }

      if (requestError?.code === SESSION_IS_CLOSED) {
        clearToken()
        redirectToLogin()
      }

      if (
        !error?.config?.ignoreError &&
        error?.response?.status !== 400 &&
        requestError?.code !== SESSION_CLOSED_CODE_NEW
      ) {
        store.dispatch(
          addNotification({
            type: 'error',
            text: requestError?.message ?? 'mainCommonError',
          })
        )
      }

      Sentry.addBreadcrumb({
        type: 'info',
        category: 'xhr',
        ...(requestError && { data: error.response.data }),
        ...(!requestError && { message: error.message }),
        level: 'error',
      })

      Sentry.addBreadcrumb({
        type: 'http',
        category: 'xhr',
        data: {
          url: getFullPathUrl(error.config),
          method: getRequestMethod(error.config),
          status_code: error.response?.status,
        },
        level: 'error',
      })

      return Promise.reject(error)
    }
  )

  return instance
}
